// src/pages/coupons/CouponEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";

export default function CouponEdit() {
  const { id } = useParams();
  const nav = useNavigate();

  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  // test listesi (public /api/tests/list)
  const [tests, setTests] = useState([]);              // [{id,name}]
  const [selectedTestIds, setSelectedTestIds] = useState([]); // number[]
  const [loadingTests, setLoadingTests] = useState(false);

  // Kuponu getir
  const fetchOne = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/coupons/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      const c = res.data;
      setF({
        code: c.code,
        discount_type: c.discount_type,
        discount_value: c.discount_value,
        min_amount: c.min_amount ?? "",
        usage_limit: c.usage_limit ?? "",
        start_at: c.start_at ? c.start_at.slice(0, 16) : "",
        end_at: c.end_at ? c.end_at.slice(0, 16) : "",
        is_active: !!c.is_active,
        is_header: !!c.is_header,
      });

      // kuponun mevcut testleri (include: tests ile geliyor)
      const testIds = Array.isArray(c.tests) ? c.tests.map(t => Number(t.id)).filter(Boolean) : [];
      setSelectedTestIds(testIds);
    } catch (e) {
      console.error("❌ Kupon detayı alınamadı:", e);
      toast.error("Kupon detayı alınamadı");
    }
  };

  // Test listesini getir
  const fetchTests = async () => {
    try {
      setLoadingTests(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/tests/list?limit=1000`
      );
      const items = Array.isArray(res?.data?.data) ? res.data.data : [];
      setTests(
        items
          .filter(x => x?.id && x?.name)
          .map(x => ({ id: Number(x.id), name: String(x.name) }))
      );
    } catch (e) {
      console.error("❌ Test listesi alınamadı:", e);
      toast.error("Test listesi alınamadı");
    } finally {
      setLoadingTests(false);
    }
  };

  useEffect(() => { fetchOne(); }, [id]);
  useEffect(() => { fetchTests(); }, []);

  const onChange = (k) => (e) => {
    const v = (k === "is_active" || k === "is_header") ? e.target.checked : e.target.value;
    setF((s) => ({ ...s, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.code?.trim()) return toast.warn("Kod zorunlu");
    if (f.discount_type === "percent" && (Number(f.discount_value) < 0 || Number(f.discount_value) > 100)) {
      return toast.warn("Yüzde indirim 0-100 aralığında olmalı");
    }

    // payload normalizasyonu (boş stringler → null, sayı parse)
    const payload = {
      ...f,
      min_amount: f.min_amount === "" ? null : Number(f.min_amount),
      usage_limit: f.usage_limit === "" ? null : Number(f.usage_limit),
      start_at: f.start_at || null,
      end_at: f.end_at || null,
      test_ids: selectedTestIds, // çoklu test id’leri
    };

    try {
      setBusy(true);
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/coupons/${id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      toast.success("Kayıt güncellendi!");
      setTimeout(() => nav("/coupon/table"), 800);
    } catch (e) {
      console.error("❌ Kupon güncellenemedi:", e);
      const msg = e?.response?.data?.message || "Kupon güncellenemedi";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const testOptions = useMemo(
    () => tests.map(t => ({ value: t.id, label: t.name })),
    [tests]
  );
  const selectedOptions = useMemo(
    () => testOptions.filter(o => selectedTestIds.includes(o.value)),
    [testOptions, selectedTestIds]
  );

  const selectAll = () => setSelectedTestIds(tests.map(t => t.id));
  const clearAll = () => setSelectedTestIds([]);

  if (!f) {
    return (
      <div className="container py-4">
        <ToastContainer />
        <div className="alert alert-light">Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <ToastContainer />

      <form onSubmit={submit}>
        <div className="row g-4">
          {/* Sol taraf: temel bilgiler */}
          <div className="col-12 col-lg-8">
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex align-items-center justify-content-between">
                <h6 className="m-0">Temel Bilgiler</h6>
                <span className="badge bg-light text-dark">
                  {f.discount_type === "percent" ? "% Yüzde" : "₺ Tutar"}
                </span>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Kod <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      value={f.code}
                      onChange={onChange("code")}
                      placeholder="Örn: YAZ10"
                      required
                    />
                    <small className="text-muted">Kullanıcıların gireceği kupon kodu.</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">İndirim Tipi</label>
                    <div className="btn-group w-100" role="group" aria-label="İndirim tipi">
                      <button
                        type="button"
                        className={`btn ${f.discount_type === "percent" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setF(s => ({ ...s, discount_type: "percent" }))}
                        title="Yüzde"
                      >
                        % Yüzde
                      </button>
                      <button
                        type="button"
                        className={`btn ${f.discount_type === "fixed" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setF(s => ({ ...s, discount_type: "fixed" }))}
                        title="Sabit Tutar"
                      >
                        ₺ Tutar
                      </button>
                    </div>
                    <small className="text-muted d-block mt-2">
                      {f.discount_type === "percent" ? "Örn: 10 = %10 indirim" : "Örn: 50 = 50₺ indirim"}
                    </small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">İndirim Değeri <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text" style={{minWidth: 48, justifyContent: "center"}}>
                        {f.discount_type === "percent" ? "%" : "₺"}
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={f.discount_value}
                        onChange={onChange("discount_value")}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Min. Sepet Tutarı (opsiyonel)</label>
                    <div className="input-group">
                      <span className="input-group-text">₺</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={f.min_amount}
                        onChange={onChange("min_amount")}
                        placeholder="Örn: 200.00"
                      />
                    </div>
                    <small className="text-muted">Bu tutarın altındaki siparişlerde kupon uygulanmaz.</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Kullanım Limiti (opsiyonel)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={f.usage_limit}
                      onChange={onChange("usage_limit")}
                      placeholder="Örn: 100 (boş = sınırsız)"
                    />
                    <small className="text-muted">Kupon toplamda en fazla kaç kez kullanılabilir?</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Başlangıç</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={f.start_at}
                      onChange={onChange("start_at")}
                    />
                    <small className="text-muted">Boş bırakırsanız hemen geçerli olur.</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Bitiş</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={f.end_at}
                      onChange={onChange("end_at")}
                    />
                    <small className="text-muted">Boş bırakırsanız süresiz olur.</small>
                  </div>

                  {/* === Test seçimi (React-Select, çoklu) === */}
                  <div className="col-12">
                    <label className="form-label d-flex justify-content-between align-items-center">
                      <span>Geçerli Olduğu Testler</span>
                      <span className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={selectAll}
                          disabled={loadingTests || !tests.length}
                        >
                          Tümünü Seç
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={clearAll}
                          disabled={loadingTests}
                        >
                          Temizle
                        </button>
                      </span>
                    </label>

                    <Select
                      isMulti
                      isLoading={loadingTests}
                      options={testOptions}
                      value={selectedOptions}
                      onChange={(opts) => setSelectedTestIds((opts || []).map(o => o.value))}
                      placeholder="Test seçin…"
                      classNamePrefix="rs"
                      noOptionsMessage={() => loadingTests ? "Yükleniyor…" : "Kayıt bulunamadı"}
                      styles={{
                        control: (base) => ({ ...base, minHeight: 42, borderColor: "#dee2e6" }),
                        menu: (base) => ({ ...base, zIndex: 9 }),
                      }}
                    />

                    <small className="text-muted d-block mt-2">
                      Hiç seçim yapmazsan kupon <strong>hiçbir</strong> testte geçerli olmaz.
                    </small>
                  </div>
                  {/* === /Test seçimi === */}
                </div>
              </div>
            </div>
          </div>

          {/* Sağ taraf: durumlar / özet */}
          <div className="col-12 col-lg-4">
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <h6 className="m-0">Durum</h6>
              </div>
              <div className="card-body">
                <div className="form-check form-switch mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="is_active"
                    checked={f.is_active}
                    onChange={onChange("is_active")}
                  />
                  <label className="form-check-label" htmlFor="is_active">Aktif</label>
                </div>

                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="is_header"
                    checked={f.is_header}
                    onChange={onChange("is_header")}
                  />
                  <label className="form-check-label" htmlFor="is_header">Header’da Göster</label>
                </div>

                <hr className="my-4" />

                <div className="small text-muted">
                  <div className="d-flex justify-content-between">
                    <span>Kod</span>
                    <strong>{f.code || "-"}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Tip</span>
                    <strong>{f.discount_type === "percent" ? "Yüzde" : "Tutar"}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Değer</span>
                    <strong>
                      {f.discount_type === "percent"
                        ? `%${Number(f.discount_value || 0)}`
                        : `${Number(f.discount_value || 0).toLocaleString("tr-TR", {minimumFractionDigits:2, maximumFractionDigits:2})} ₺`}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Aksiyonlar */}
            <div className="card shadow-sm">
              <div className="card-body d-flex gap-2 justify-content-between">
                <Link to="/coupon/table" className="btn btn-light">İptal</Link>
                <button className="btn btn-primary" disabled={busy}>
                  {busy ? "Kaydediliyor…" : "Kaydet"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

    </div>
  );
}
